/**
 * Distances, bearings and midpoints between two points on the Earth.
 *
 * Two models:
 * - A sphere of the IUGG mean radius R₁ = (2a + b) / 3 = 6371.0088 km, where distances come
 *   from the haversine formula. Simple, always converges, and off by up to about 0.5 % because
 *   the Earth is flattened at the poles.
 * - The WGS84 ellipsoid (a = 6378137 m, 1/f = 298.257223563), where distances come from
 *   Vincenty's inverse formula (T. Vincenty, "Direct and inverse solutions of geodesics on the
 *   ellipsoid with application of nested equations", Survey Review 23(176), 1975), accurate to
 *   about half a millimetre. It can fail to converge for nearly antipodal points.
 */

export const WGS84_A = 6_378_137;
export const WGS84_F = 1 / 298.257_223_563;
export const WGS84_B = WGS84_A * (1 - WGS84_F);
/** IUGG mean radius R₁ = (2a + b) / 3, metres. */
export const EARTH_MEAN_RADIUS_M = (2 * WGS84_A + WGS84_B) / 3;

export interface LatLon {
  /** Degrees north, −90 to 90. */
  lat: number;
  /** Degrees east, −180 to 180. */
  lon: number;
}

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;

/** Degrees wrapped into [0, 360). */
export function normalizeBearing(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

/** Degrees wrapped into (−180, 180]. */
export function normalizeLongitude(degrees: number): number {
  const wrapped = normalizeBearing(degrees + 180) - 180;
  return wrapped === -180 ? 180 : wrapped;
}

/** Haversine great-circle distance in metres on a sphere of `radius`. */
export function haversineDistance(
  a: LatLon,
  b: LatLon,
  radius = EARTH_MEAN_RADIUS_M
): number {
  const phi1 = a.lat * RAD;
  const phi2 = b.lat * RAD;
  const dPhi = (b.lat - a.lat) * RAD;
  const dLambda = (b.lon - a.lon) * RAD;
  const h =
    Math.sin(dPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return 2 * radius * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** The intermediate quantity `a` of the haversine formula, for showing the working. */
export function haversineTerm(a: LatLon, b: LatLon): number {
  const dPhi = (b.lat - a.lat) * RAD;
  const dLambda = (b.lon - a.lon) * RAD;
  return (
    Math.sin(dPhi / 2) ** 2 +
    Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLambda / 2) ** 2
  );
}

/** Initial great-circle bearing from a to b, degrees clockwise from north, [0, 360). */
export function initialBearing(a: LatLon, b: LatLon): number {
  const phi1 = a.lat * RAD;
  const phi2 = b.lat * RAD;
  const dLambda = (b.lon - a.lon) * RAD;
  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);
  return normalizeBearing(Math.atan2(y, x) * DEG);
}

/** The bearing at b when arriving from a along the great circle. */
export function finalBearing(a: LatLon, b: LatLon): number {
  return normalizeBearing(initialBearing(b, a) + 180);
}

/** The great-circle midpoint of a and b. */
export function midpoint(a: LatLon, b: LatLon): LatLon {
  const phi1 = a.lat * RAD;
  const phi2 = b.lat * RAD;
  const dLambda = (b.lon - a.lon) * RAD;
  const bx = Math.cos(phi2) * Math.cos(dLambda);
  const by = Math.cos(phi2) * Math.sin(dLambda);
  const phi = Math.atan2(
    Math.sin(phi1) + Math.sin(phi2),
    Math.sqrt((Math.cos(phi1) + bx) ** 2 + by ** 2)
  );
  const lambda = a.lon * RAD + Math.atan2(by, Math.cos(phi1) + bx);
  return { lat: phi * DEG, lon: normalizeLongitude(lambda * DEG) };
}

export interface VincentyResult {
  /** Metres along the geodesic. */
  distance: number;
  /** Forward azimuth at the first point, degrees [0, 360). */
  initialAzimuth: number;
  /** Forward azimuth at the second point, degrees [0, 360). */
  finalAzimuth: number;
  iterations: number;
}

const VINCENTY_TOLERANCE = 1e-12;
const VINCENTY_MAX_ITERATIONS = 200;

/** Vincenty's inverse formula on WGS84, or null if it fails to converge (near-antipodal points). */
export function vincentyInverse(p1: LatLon, p2: LatLon): VincentyResult | null {
  const a = WGS84_A;
  const b = WGS84_B;
  const f = WGS84_F;
  const L = (p2.lon - p1.lon) * RAD;
  const U1 = Math.atan((1 - f) * Math.tan(p1.lat * RAD));
  const U2 = Math.atan((1 - f) * Math.tan(p2.lat * RAD));
  const sinU1 = Math.sin(U1);
  const cosU1 = Math.cos(U1);
  const sinU2 = Math.sin(U2);
  const cosU2 = Math.cos(U2);

  let lambda = L;
  let iterations = 0;
  let sinSigma = 0;
  let cosSigma = 0;
  let sigma = 0;
  let cosSqAlpha = 0;
  let cos2SigmaM = 0;
  let sinLambda = 0;
  let cosLambda = 0;
  let delta = Number.POSITIVE_INFINITY;
  while (delta > VINCENTY_TOLERANCE && iterations < VINCENTY_MAX_ITERATIONS) {
    sinLambda = Math.sin(lambda);
    cosLambda = Math.cos(lambda);
    sinSigma = Math.sqrt(
      (cosU2 * sinLambda) ** 2 +
        (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) ** 2
    );
    if (sinSigma === 0) {
      return { distance: 0, initialAzimuth: 0, finalAzimuth: 0, iterations };
    }
    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
    sigma = Math.atan2(sinSigma, cosSigma);
    const sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma;
    cosSqAlpha = 1 - sinAlpha * sinAlpha;
    // Both points on the equator: cos²α = 0 and cos 2σm is taken as 0.
    cos2SigmaM =
      cosSqAlpha === 0 ? 0 : cosSigma - (2 * sinU1 * sinU2) / cosSqAlpha;
    const C = (f / 16) * cosSqAlpha * (4 + f * (4 - 3 * cosSqAlpha));
    const previous = lambda;
    lambda =
      L +
      (1 - C) *
        f *
        sinAlpha *
        (sigma +
          C *
            sinSigma *
            (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)));
    delta = Math.abs(lambda - previous);
    iterations += 1;
  }
  if (delta > VINCENTY_TOLERANCE) {
    return null;
  }
  const uSq = (cosSqAlpha * (a * a - b * b)) / (b * b);
  const A =
    1 + (uSq / 16_384) * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const B = (uSq / 1024) * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const deltaSigma =
    B *
    sinSigma *
    (cos2SigmaM +
      (B / 4) *
        (cosSigma * (-1 + 2 * cos2SigmaM ** 2) -
          (B / 6) *
            cos2SigmaM *
            (-3 + 4 * sinSigma ** 2) *
            (-3 + 4 * cos2SigmaM ** 2)));
  const distance = b * A * (sigma - deltaSigma);
  const alpha1 = Math.atan2(
    cosU2 * sinLambda,
    cosU1 * sinU2 - sinU1 * cosU2 * cosLambda
  );
  const alpha2 = Math.atan2(
    cosU1 * sinLambda,
    -sinU1 * cosU2 + cosU1 * sinU2 * cosLambda
  );
  return {
    distance,
    initialAzimuth: normalizeBearing(alpha1 * DEG),
    finalAzimuth: normalizeBearing(alpha2 * DEG),
    iterations,
  };
}

/** Index 0–15 of the 16-point compass rose (N, NNE, NE, …) nearest a bearing. */
export function compassIndex(bearing: number): number {
  return Math.round(normalizeBearing(bearing) / 22.5) % 16;
}

export type DistanceUnit = "km" | "mi" | "nmi" | "m";

const METRES_PER_UNIT: Record<DistanceUnit, number> = {
  km: 1000,
  mi: 1609.344,
  nmi: 1852,
  m: 1,
};

export function metresTo(metres: number, unit: DistanceUnit): number {
  return metres / METRES_PER_UNIT[unit];
}

export function toMetres(value: number, unit: DistanceUnit): number {
  return value * METRES_PER_UNIT[unit];
}
